<script lang="ts" setup>
import UserAvatar from "@/components/UserAvatar.vue";
import { TextEllipsis } from "vant";
import { CoinBalanceRecordEntityType } from "@/types/coin";
import { computed, onBeforeMount, ref } from "vue";
import { useDefaultAvatar, usePreviewImg } from "@/use/common";
import { useI18n } from "vue-i18n";
import { CoinRank, CoinRankItem } from "@/types/coinRank";
import { ordinalText, useCoinRank } from "@/stores/coinRank";
import { useRouter } from "vue-router";
import { getRankServerDtStart, useRankText } from "@/composable/rank";
import {
  ValidRankEntityType,
  VoteTargetType,
} from "@/composable/rank/rankConfig";

const props = defineProps<{
  id?: number;
  entityType: ValidRankEntityType;
  entity: VoteTargetType;
}>();

const { t } = useI18n();
const rank = useCoinRank();
const router = useRouter();

const title = computed(() => {
  switch (props.entityType) {
    case CoinBalanceRecordEntityType.Collection:
    case CoinBalanceRecordEntityType.StoryBook:
      return (props.entity as StoryBook).title;
    case CoinBalanceRecordEntityType.Game:
    case CoinBalanceRecordEntityType.Mate:
      return (props.entity as GalGame).name;
    default:
      return "";
  }
});
const bannerUrl = computed(() => {
  switch (props.entityType) {
    case CoinBalanceRecordEntityType.Collection:
    case CoinBalanceRecordEntityType.Game:
      return (props.entity as GalGame).coverMedia?.url;
    case CoinBalanceRecordEntityType.Mate:
      return (props.entity as Mate).mediaUrl;
    case CoinBalanceRecordEntityType.StoryBook:
      return (props.entity as StoryBook).media?.url;
    default:
      return null;
  }
});
const author = computed<Mate | null>(() => {
  switch (props.entityType) {
    case CoinBalanceRecordEntityType.StoryBook:
    case CoinBalanceRecordEntityType.Game:
      return (props.entity as StoryBook).mate;
    case CoinBalanceRecordEntityType.Mate:
    default:
      return null;
  }
});
const description = computed(() => {
  switch (props.entityType) {
    case CoinBalanceRecordEntityType.StoryBook:
      return (props.entity as StoryBook).description;
    case CoinBalanceRecordEntityType.Game:
      return (props.entity as GalGame).description;
    case CoinBalanceRecordEntityType.Mate:
      return (props.entity as Mate).bio || t("Message.notBio");
    default:
      return "";
  }
});

const gradientStyle = "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))";
const bannerStyle = computed(() => {
  const url = bannerUrl.value;
  if (url) {
    return {
      filter: "blur(3px)",
      background: `${gradientStyle}, url(${url}) no-repeat`,
      backgroundSize: "cover",
    };
  } else {
    return {
      filter: "blur(3px)",
      backgroundImage:
        "linear-gradient(45deg, rgb(81 7 115) 0%, rgb(196, 58, 48) 100%)",
      backgroundSize: "cover",
    };
  }
});

const currentRank = computed<CoinRank>(() =>
  props.id ? rank.getRankById(props.id)! : rank.currentCoinRank!
);
const rankItem = ref<CoinRankItem | null>(null);
const loading = ref(false);
onBeforeMount(async () => {
  loading.value = true;
  try {
    rankItem.value = await rank.fetchRankItem(
      props.entityType,
      props.entity.id!,
      props.id ?? rank.currentCoinRank!.id
    );
  } finally {
    loading.value = false;
  }
});
const goToEntityHome = () => {
  switch (props.entityType) {
    case CoinBalanceRecordEntityType.Game:
      router.push({
        name: "galGameDetail",
        params: { uuid: props.entity.uuid },
      });
      break;
    case CoinBalanceRecordEntityType.Mate:
      router.push({ name: "mateHome", params: { uuid: props.entity.uuid } });
      break;
    case CoinBalanceRecordEntityType.StoryBook:
      router.push({
        name: "storyBookHome",
        params: { uuid: props.entity.uuid },
      });
      break;
    case CoinBalanceRecordEntityType.Collection:
      router.push({
        name: "collectionMain",
        params: { uuid: props.entity.uuid },
      });
      break;
  }
};
</script>
<template>
  <div>
    <div class="relative flex flex-col overflow-hidden">
      <div class="flex justify-center items-center gap-4 relative z-10">
        <div class="relative flex">
          <VanImage
            :src="bannerUrl ? usePreviewImg(bannerUrl) : useDefaultAvatar()"
            alt=""
            class="h-[206px] w-[206px] bg-primary"
            fit="cover"
            crossorigin="anonymous"
          />
        </div>
      </div>
      <div
        :style="bannerStyle"
        class="absolute top-0 bottom-0 left-0 right-0 z-0"
      />
      <div
        class="absolute bottom-0 w-full py-4 px-8 bg-neutral/60 flex justify-between z-20"
      >
        <div class="flex flex-col gap-1">
          <span>{{ useRankText(currentRank!) }}</span>
          <span v-if="loading" class="loading loading-sm" />
          <span v-else class="text-lg font-bold">{{
            rankItem ? ordinalText(rankItem.position) : $t("rank.notRanked")
          }}</span>
        </div>
        <div class="flex items-end flex-col justify-center gap-1">
          <span class="flex gap-1">
            <i class="iconify mdi--ticket"></i>
            {{ $t("rank.getVotesCount") }}
          </span>
          <span v-if="loading" class="loading loading-sm"></span>
          <span v-else class="text-base">{{ rankItem?.voteCount ?? 0 }}</span>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-3 flex-grow overflow-hidden p-2.5">
      <div class="flex items-center gap-2">
        <div class="text-lg font-bold overflow-hidden line-clamp-1">
          {{ title }}
        </div>
        <button
          class="btn btn-neutral btn-sm rounded text-base-content/50"
          @click="goToEntityHome"
        >
          <i class="iconfont icon-home" />
          {{
            entityType === CoinBalanceRecordEntityType.Mate
              ? $t("rank.mateHome")
              : $t("rank.contentHome")
          }}
        </button>
      </div>

      <div
        v-if="author"
        class="flex gap-2 items-center w-full overflow-hidden cursor-pointer"
        @click="
          router.push({ name: 'mateHome', params: { uuid: author.uuid } })
        "
      >
        <user-avatar :avatar-url="author.mediaUrl" class="h-6 shrink-0" />
        <div class="text-base-content/50 w-full text-overflow-ellipsis">
          {{ author.name }}
        </div>
      </div>
      <div class="text-base-content/50" @click="$event.stopPropagation()">
        <text-ellipsis
          :collapse-text="$t('Buttons.collapse')"
          :content="description"
          :expand-text="$t('Buttons.expand')"
          class="text-base-content/50 story-desc"
          rows="2"
        />
      </div>
    </div>
  </div>
</template>
